import { useEffect, useState, useRef, useCallback } from 'react';
import { showEmailNotification } from '@/components/ui/EmailNotification';
import { requestNotificationPermission, onMessageListener } from '@/config/firebase';
import { useFolderStore } from '@/store/folderStore';
import api from '@/services/api';
import { authService } from '@/services/auth';
import { MessagePayload } from 'firebase/messaging';

// Create a custom event for email updates
export const EMAIL_UPDATE_EVENT = 'email-update-received';

interface NotificationPayload {
  notification: {
    title: string;
    body: string;
  };
  data: {
    type: 'new_messages';
    email: string;
    server: string;
    folder: string;
    newCount: string;
    totalCount: string;
    timestamp: string;
    click_action: string;
    email_id: string;
    email_uid: string;
  };
}

export const useFirebaseNotification = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [newEmail, setNewEmail] = useState<NotificationPayload | null>(null);
  const { updateFolderCounts } = useFolderStore();
  const notificationQueueRef = useRef<NotificationPayload[]>([]);
  const isProcessingRef = useRef(false);

  // Use useRef to maintain a single instance of Audio
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio on mount
  useEffect(() => {
    notificationSoundRef.current = new Audio('/notification.mp3');
    notificationSoundRef.current.load(); // Preload the audio

    // Cleanup on unmount
    return () => {
      if (notificationSoundRef.current) {
        notificationSoundRef.current.pause();
        notificationSoundRef.current = null;
      }
    };
  }, []);

  // Process notification queue
  const processNotificationQueue = useCallback(async () => {
    if (isProcessingRef.current || notificationQueueRef.current.length === 0) {
      return;
    }

    // console.log('Processing notification queue', notificationQueueRef.current);

    isProcessingRef.current = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    try {
      const notification = notificationQueueRef.current[0];

      // console.log('Processing notification', notification);
      
      // Update folder counts
      if (notification.data.folder) {
        try {
          await updateFolderCounts(
            notification.data.folder,
            parseInt(notification.data.newCount, 10),
            parseInt(notification.data.totalCount, 10),
            parseInt(notification.data.newCount, 10)
          );
        } catch (err) {
          console.error('Failed to update folder counts:', err);
          // Don't throw here, continue with notification
        }
      }

      // Update folder counts directly instead of fetching all folders
      if (notification.data.folder && notification.data.totalCount !== undefined) {
        updateFolderCounts(
          notification.data.folder,
          parseInt(notification.data.newCount || '0'),
          parseInt(notification.data.totalCount),
          parseInt(notification.data.newCount || '0') // Use newCount as unseenCount since they're typically the same
        );
      }
      
      // Fetch new email details and update local storage
      if (notification.data.email_id) {
        try {
          // Fetch email details
          // console.log('Fetching email by ID:', notification.data.email_id);
          const response = await api.get(`/api/email/get_by_id/${notification.data.email_id}`);
          const newEmail = response.data;
          // console.log('Received new email:', newEmail);

          // Get current folder's emails from localStorage
          const localKey = `emails_${notification.data.folder}_1`; // Page 1
          const cached = localStorage.getItem(localKey);

          if (cached) {
            try {
              const parsedCache = JSON.parse(cached);
              
              // Add new email to the beginning of the list
              const updatedEmails = {
                ...parsedCache,
                emails: [newEmail, ...(parsedCache.emails || [])]
              };

              // Update localStorage
              localStorage.setItem(localKey, JSON.stringify(updatedEmails));
              
              // Create event detail with all required data
              const eventDetail = {
                type: notification.data.type,
                folder: notification.data.folder,
                email_data: newEmail,
                email_id: notification.data.email_id,
                email_uid: notification.data.email_uid,
                click_action: notification.data.click_action,
                newCount: notification.data.newCount,
                totalCount: notification.data.totalCount,
                timestamp: notification.data.timestamp
              };
              
              // console.log('Dispatching event with detail:', eventDetail);
              window.dispatchEvent(new CustomEvent(EMAIL_UPDATE_EVENT, { detail: eventDetail }));
            } catch (err) {
              console.error('Failed to update localStorage:', err);
            }
          }
        } catch (err) {
          console.error('Failed to fetch and update new email:', err);
          // Don't throw here, continue with notification
        }
      }

      // Dispatch custom event
      try {
        window.dispatchEvent(new CustomEvent(EMAIL_UPDATE_EVENT, { detail: notification.data }));
      } catch (err) {
        console.error('Failed to dispatch email update event:', err);
        // Don't throw here, continue with notification
      }

      // Play sound
      if (notificationSoundRef.current) {
        notificationSoundRef.current.currentTime = 0;
        try {
          await notificationSoundRef.current.play();
        } catch (err) {
          console.warn('Could not play notification sound:', err);
          // Don't throw here, continue with notification
        }
      }

      // Show toast notification
      try {
          showEmailNotification({
            title: notification.notification.title,
            body: notification.notification.body,
            folder: notification.data.folder,
            email_id: notification.data.email_id,
            email_uid: notification.data.email_uid,
            newCount: notification.data.newCount,
            totalCount: notification.data.totalCount
          });

        // Wait for toast to complete - reduced from 5s to 2s
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 2000);
        });

        // Remove processed notification only after successful processing
        notificationQueueRef.current.shift();
        
        // Process next notification if any
        if (notificationQueueRef.current.length > 0) {
          setTimeout(processNotificationQueue, 500); // Add small delay between notifications
        }
      } catch (error) {
        console.error('Failed to show notification:', error);
        retryCount++;
        
        if (retryCount < MAX_RETRIES) {
          // Retry after a short delay
          setTimeout(processNotificationQueue, 1000);
          return;
        } else {
          console.error(`Failed to process notification after ${MAX_RETRIES} retries:`, notification);
          // Remove failed notification after max retries
          notificationQueueRef.current.shift();
        }
      }
    } catch (error) {
      console.error('Error processing notification:', error);
      retryCount++;
      
      if (retryCount < MAX_RETRIES) {
        // Retry after a short delay
        setTimeout(processNotificationQueue, 1000);
        return;
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [updateFolderCounts]);

  useEffect(() => {
    let retryCount = 0;
    const MAX_RETRIES = 3;
    
    const initializeFirebase = async () => {
      try {
        const user = authService.getCurrentUser();
        if (!user?.username) {
          console.warn('No user email found, notifications will not be initialized');
          return;
        }

        // Check if notifications are supported
        if (!('Notification' in window)) {
          console.warn('This browser does not support notifications');
          setIsInitialized(false);
          return;
        }

        // Check if service workers are supported
        if (!('serviceWorker' in navigator)) {
          console.warn('Service workers are not supported, notifications will not work');
          setIsInitialized(false);
          return;
        }

        // Check if push service is available
        try {
          const registration = await navigator.serviceWorker.ready;
          const pushManager = await registration.pushManager.getSubscription();
          if (!pushManager && !('safari' in window)) { // Safari handles push differently
            console.warn('Push service not available in this browser');
            setIsInitialized(false);
            return;
          }
        } catch (err) {
          if ((err as Error).name === 'AbortError' || (err as Error).message?.includes('push service not available')) {
            console.warn('Push service not available:', (err as Error).message);
            setIsInitialized(false);
            return; // Don't retry for push service unavailability
          }
          throw err; // Re-throw other errors to be handled in the catch block
        }

        const token = await requestNotificationPermission();
        if (!token) {
          console.warn('Failed to get notification token');
          setIsInitialized(false);
          return;
        }

        try {
          await api.post('/api/notifications/register', { token });
        } catch (err) {
          if ((err as Error).name === 'AbortError' || (err as Error).message?.includes('push service not available')) {
            console.warn('Failed to register notification token - push service not available');
            setIsInitialized(false);
            return; // Don't retry for push service unavailability
          }
          console.error('Failed to register notification token with server:', err);
          throw err; // Re-throw other errors to be handled in the catch block
        }
        
        setIsInitialized(true);
        console.log('Firebase notifications initialized successfully');
        retryCount = 0; // Reset retry count on success
      } catch (error) {
        // Don't retry for specific errors
        if ((error as Error).name === 'AbortError' || (error as Error).message?.includes('push service not available')) {
          console.warn('Push notification service not available - notifications will be disabled');
          setIsInitialized(false);
          return;
        }

        console.error(`Error initializing Firebase (attempt ${retryCount + 1}/${MAX_RETRIES}):`, error);
        retryCount++;
        
        // Only retry if we haven't exceeded max retries
        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying Firebase initialization in 5 seconds... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
          setTimeout(initializeFirebase, 5000);
        } else {
          console.error('Failed to initialize Firebase after maximum retry attempts');
          setIsInitialized(false);
        }
      }
    };

    initializeFirebase();

    // Cleanup function
    return () => {
      setIsInitialized(false);
      retryCount = MAX_RETRIES; // Prevent any pending retries
    };
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    // Handle foreground messages
    const messageHandler = (payload: MessagePayload) => {
      const notificationPayload = payload as unknown as NotificationPayload;
      setNewEmail(notificationPayload);

      // Add to queue and process
      notificationQueueRef.current.push(notificationPayload);
      processNotificationQueue();
    };

    const unsubscribe = onMessageListener(messageHandler);

    return () => {
      unsubscribe();
      // Clear queue on unmount
      notificationQueueRef.current = [];
      isProcessingRef.current = false;
    };
  }, [isInitialized, processNotificationQueue]);

  return { isInitialized, newEmail };
}; 